import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  civs, maps, getMapImage,
  MATCH_STATUS_REVIEWED, MATCH_STATUS_NOT_STARTED,
  AW_PLAYER_A, AW_PLAYER_B, AW_BOTH_PLAYERS, AW_REMOVE,
} from "../utils/constants";
import { useNavContext } from "../context/nav_context";
import { Loading, PlayerProfileCard, StreamerMenu } from "../components";
import { updateMatchByAdmin } from "../services/matchService";
import useMatch from "../hooks/useMatch";
import usePlayers from "../hooks/usePlayers";
import useDivision from "../hooks/useDivision";
import usePlayerRegularDivision from "../hooks/usePlayerRegularDivision";
import useTournamentDetails from "../hooks/useTournamentDetails";
import useMatchForm from "../hooks/useMatchForm";
import MatchHeader from "../components/match/sections/MatchHeader";
import AdminMatchPanel from "../components/match/sections/AdminMatchPanel";
import DateSection from "../components/match/sections/DateSection";
import DraftSection from "../components/match/sections/DraftSection";
import GameFormatInfo from "../components/match/sections/GameFormatInfo";
import MatchResultsForm from "../components/match/sections/MatchResultsForm";
import RecSection from "../components/match/sections/RecSection";
import CivSelectorOverlay from "../components/match/sections/CivSelectorOverlay";
import MapSelectorOverlay from "../components/match/sections/MapSelectorOverlay";

const MatchPage = () => {
  const { matchId } = useParams();
  const { user, accessToken } = useNavContext();

  const { match, loading: loadingMatch, error: errorMatch } = useMatch(matchId);
  const { playerA, playerB, loading: loadingPlayers } = usePlayers(
    match?.playerAid, match?.playerBid
  );
  const { division } = useDivision(match?.divisionId);
  const isPlayoff = match?.phase === "PLAYOFF";
  const { info: playerARegularDivision } = usePlayerRegularDivision(
    isPlayoff ? match?.playerAid : null,
    isPlayoff ? match?.tournamentId : null
  );
  const { info: playerBRegularDivision } = usePlayerRegularDivision(
    isPlayoff ? match?.playerBid : null,
    isPlayoff ? match?.tournamentId : null
  );
  const { details: tournamentDetails, loading: tournamentDetailsLoading } =
    useTournamentDetails(match?.tournamentId);

  const { state, dispatch, initForm, publish } = useMatchForm(match);

  const [activeOverlay, setActiveOverlay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const lastSaveFn = useRef(null);

  useEffect(() => { initForm(); }, [initForm]);
  useEffect(() => {
    if (!saveSuccess) return;
    const t = setTimeout(() => setSaveSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [saveSuccess]);

  const isPublic = useMemo(() => {
    if (!match || !user) return true;
    return !(
      match.playerAid === user.playerId ||
      match.playerBid === user.playerId ||
      user.isAdmin || user.isModerator
    );
  }, [match?.playerAid, match?.playerBid, user]);

  const isAdmin = useMemo(
    () => !!(user?.isAdmin || user?.isModerator),
    [user?.isAdmin, user?.isModerator]
  );

  const civilizations = useMemo(
    () => [...civs].sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
    ),
    []
  );

  const currentGame = (state.games && state.games[state.gameStep]) || {};

  const getMapImageUrl = useCallback((mapName) => {
    if (tournamentDetails?.maps) {
      const found = tournamentDetails.maps.find(
        m => m.mapName === mapName || m.name === mapName
      );
      if (found) return found.mapImage || found.image;
    }
    return getMapImage(mapName, maps);
  }, [tournamentDetails]);

  const doSave = useCallback(async (saveFn) => {
    setSaving(true);
    setSaveError(false);
    lastSaveFn.current = saveFn;
    try {
      const ok = await saveFn();
      if (ok) setSaveSuccess(true);
      else setSaveError(true);
    } catch { setSaveError(true); }
    finally { setSaving(false); }
  }, []);

  const handlePublishUpdate = useCallback(
    () => doSave(publish),
    [doSave, publish]
  );

  const doAdminSave = useCallback(async (payload) => {
    setSaving(true);
    setSaveError(false);
    try {
      await updateMatchByAdmin(matchId, payload, accessToken);
      dispatch({ type: "PUBLISH_ACK", payload });
      setSaveSuccess(true);
    } catch { setSaveError(true); }
    finally { setSaving(false); }
  }, [matchId, accessToken, dispatch]);

  const handleAWOption = useCallback((option) => {
    dispatch({ type: "SET_ADMIN_WIN", payload: option });
    let updates;
    if (option === AW_PLAYER_A)
      updates = { playerAaw: "AW", playerBaw: "", playerAresult: 3, playerBresult: 0, status: MATCH_STATUS_REVIEWED };
    else if (option === AW_PLAYER_B)
      updates = { playerAaw: "", playerBaw: "AW", playerAresult: 0, playerBresult: 3, status: MATCH_STATUS_REVIEWED };
    else if (option === AW_BOTH_PLAYERS)
      updates = { playerAaw: "AL", playerBaw: "AL", playerAresult: 0, playerBresult: 0, status: MATCH_STATUS_REVIEWED };
    else if (option === AW_REMOVE)
      updates = { playerAaw: "", playerBaw: "", playerAresult: 0, playerBresult: 0, status: MATCH_STATUS_NOT_STARTED };
    if (updates) doAdminSave({ ...match, ...updates });
  }, [match, doAdminSave, dispatch]);

  const handleMarkReviewed = useCallback(() => {
    dispatch({ type: "MARK_REVIEWED" });
    doAdminSave({ ...match, status: MATCH_STATUS_REVIEWED });
  }, [match, doAdminSave, dispatch]);

  const handleDeleteResults = useCallback(() => {
    dispatch({ type: "DELETE_RESULTS" });
    if (match?.games) {
      const games = match.games.map(g => ({ ...g, gameWinner: null }));
      doAdminSave({ ...match, playerAresult: 0, playerBresult: 0, games });
    }
  }, [match, doAdminSave, dispatch]);

  const handleSelectMap = useCallback((value) => {
    dispatch({ type: "SET_GAME_FIELD", payload: { step: state.gameStep, field: "map", value } });
    setActiveOverlay(null);
  }, [state.gameStep, dispatch]);

  const handleSelectCiv = useCallback((side, civName) => {
    dispatch({
      type: "SET_GAME_FIELD",
      payload: { step: state.gameStep, field: side === "A" ? "gameCivA" : "gameCivB", value: civName },
    });
    setActiveOverlay(null);
  }, [state.gameStep, dispatch]);

  const handleSelectWinner = useCallback((playerId) => {
    dispatch({ type: "SET_GAME_FIELD", payload: { step: state.gameStep, field: "gameWinner", value: playerId } });
  }, [state.gameStep, dispatch]);

  if (loadingMatch || loadingPlayers || !state._match) return <Loading />;
  if (errorMatch) return <p>Error cargando la información del partido.</p>;

  return (
    <Page>
      {saving ? (
        <Snackbar style={{ background: "rgba(82,0,255,0.85)" }}>
          <Loading />
        </Snackbar>
      ) : null}
      {saveError ? (
        <Snackbar
          onClick={() => lastSaveFn.current && doSave(lastSaveFn.current)}
          style={{ background: "rgba(255,59,59,0.85)" }}
        >
          Error al publicar. Click para reintentar.
        </Snackbar>
      ) : null}
      {saveSuccess ? (
        <Snackbar style={{ background: "rgba(60,255,208,0.85)" }}>
          <SnackbarText style={{ color: "#000" }}>
            Resultados actualizados correctamente.
          </SnackbarText>
        </Snackbar>
      ) : null}
      {!isPublic && state.changesPending && !saving ? (
        <SaveSnackbar onClick={handlePublishUpdate}>
          <SaveText className="bounce">Guardar cambios</SaveText>
        </SaveSnackbar>
      ) : null}

      {activeOverlay === "map" && !tournamentDetailsLoading && tournamentDetails?.maps ? (
        <MapSelectorOverlay
          maps={tournamentDetails.maps}
          onSelect={handleSelectMap}
          onClose={() => setActiveOverlay(null)}
        />
      ) : null}
      {activeOverlay === "civA" ? (
        <CivSelectorOverlay
          civs={civilizations}
          side="A"
          onSelect={(v) => handleSelectCiv("A", v)}
          onClose={() => setActiveOverlay(null)}
        />
      ) : null}
      {activeOverlay === "civB" ? (
        <CivSelectorOverlay
          civs={civilizations}
          side="B"
          onSelect={(v) => handleSelectCiv("B", v)}
          onClose={() => setActiveOverlay(null)}
        />
      ) : null}

      <ColumnLeft>
        <MatchHeader
          match={match}
          playerA={playerA}
          playerB={playerB}
          division={division}
          playerARegularDivision={playerARegularDivision}
          playerBRegularDivision={playerBRegularDivision}
          matchResultA={state.matchResultA}
          matchResultB={state.matchResultB}
          matchResultAWA={state.playerAaw}
          matchResultAWB={state.playerBaw}
          matchStatus={state.matchStatus}
        />
        <PlayerProfileCard
          key={`player-card-${match.playerAid}`}
          playerId={match.playerAid}
          tournamentId={match.tournamentId}
          player={playerA}
          loadingPlayer={false}
          errorPlayer={false}
        />
        <PlayerProfileCard
          key={`player-card-${match.playerBid}`}
          playerId={match.playerBid}
          tournamentId={match.tournamentId}
          player={playerB}
          loadingPlayer={false}
          errorPlayer={false}
        />
      </ColumnLeft>

      <ColumnRight>
        <StreamerMenu
          matchId={matchId}
          tournamentId={match.tournamentId}
          isAdmin={isAdmin}
        />

        {isAdmin ? (
          <AdminMatchPanel
            playerAaw={state.playerAaw}
            playerBaw={state.playerBaw}
            playerA={playerA}
            playerB={playerB}
            matchStatus={state.matchStatus}
            onAWOption={handleAWOption}
            onMarkReviewed={handleMarkReviewed}
            onDeleteResults={handleDeleteResults}
          />
        ) : null}

        <DateSection
          isPublic={isPublic}
          matchDate={state.matchDate}
          showDateTimePicker={state.showDateTimePicker}
          onAcceptDate={(v) => dispatch({
            type: "SET_MATCH_FIELD",
            payload: { field: "matchDate", value: v },
          })}
          onTogglePicker={() => dispatch({
            type: "SET_MATCH_FIELD",
            payload: { field: "showDateTimePicker", value: true },
          })}
        />

        <DraftSection
          isPublic={isPublic}
          gamesFormat={match.gamesFormat}
          mapDraft={state.mapDraft}
          civDraft={state.civDraft}
          playerA={playerA}
          playerB={playerB}
          onChangeMapDraft={(v) => dispatch({
            type: "SET_MATCH_FIELD",
            payload: { field: "mapDraft", value: v },
          })}
          onChangeCivDraft={(v) => dispatch({
            type: "SET_MATCH_FIELD",
            payload: { field: "civDraft", value: v },
          })}
        />

        <GameFormatInfo
          gamesFormat={match.gamesFormat}
          playerA={playerA}
          playerB={playerB}
          isPublic={isPublic}
        />

        <MatchResultsForm
          isPublic={isPublic}
          gamesFormat={match.gamesFormat}
          gameStep={state.gameStep}
          civA={currentGame.gameCivA}
          civB={currentGame.gameCivB}
          map={currentGame.map}
          winner={currentGame.gameWinner}
          playerAid={match.playerAid}
          playerBid={match.playerBid}
          playerA={playerA}
          playerB={playerB}
          getMapImageUrl={getMapImageUrl}
          onSelectStep={(s) => dispatch({ type: "SET_GAME_STEP", payload: s })}
          onOpenCivA={() => setActiveOverlay("civA")}
          onOpenCivB={() => setActiveOverlay("civB")}
          onOpenMap={() => setActiveOverlay("map")}
          onSelectWinner={handleSelectWinner}
        />

        <RecSection isPublic={isPublic} division={division} />
      </ColumnRight>
    </Page>
  );
};

const Page = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 6rem;
  padding: 0 1rem;

  @media (max-width: 1100px) {
    flex-direction: column;
    padding: 0 0.5rem;
  }
`;

const ColumnLeft = styled.div`
  width: 32vw;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1100px) {
    width: 100%;
    min-width: 0;
  }
`;

const ColumnRight = styled.div`
  flex: 1;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Snackbar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0.75rem 1rem;
  z-index: 1000;
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SnackbarText = styled.span`
  font-family: var(--v-font-mono);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SaveSnackbar = styled.div`
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  cursor: pointer;
  background: var(--v-mint);
  padding: 0.85rem 2.5rem;
  border-radius: 40px;
  box-shadow: 0 0 30px rgba(60, 255, 208, 0.25);
  transition: opacity var(--v-transition);

  &:hover {
    opacity: 0.85;
  }
`;

const SaveText = styled.span`
  font-family: var(--v-font-mono);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #000;
`;

export default MatchPage;
